import { useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Check, Minus, Plus, Send, ShoppingBag } from "lucide-react";
import { api } from "../lib/apiClient";
import { toast, Toaster } from "sonner";
import CreationsGallery from "../components/CreationsGallery";

const BASE_PRICE = 14;
const NAPPAGE_PREMIUM_EXTRA = 2;

const BASES = [
    { id: "brownie", name: "Brownie" },
    { id: "cookie-noir", name: "Cookie chocolat noir Nestlé" },
    { id: "cookie-lait", name: "Cookie chocolat au lait Milka" },
    { id: "cookie-blanc", name: "Cookie chocolat blanc Galak" },
];

const NAPPAGES = [
    { id: "nutella", name: "Nutella", extra: 0 },
    { id: "speculoos", name: "Pâte à tartiner Spéculoos", extra: 0 },
    { id: "nocciolata-bianca", name: "Nocciolata Bianca", extra: NAPPAGE_PREMIUM_EXTRA },
];

const TOPPINGS = [
    "Kinder Bueno",
    "Kinder Bueno White",
    "Kinder Maxi",
    "Kinder Country",
    "Oreo",
    "M&M's",
    "Kit Kat",
    "Mini Chamallows",
    "Smarties",
    "Noix de pécan",
    "Noisettes",
    "Spéculoos",
];

const MAX_TOPPINGS = 8;

function formatPrice(price) {
    if (price == null) return "-";
    return `${Number(price).toFixed(2).replace(".", ",")} €`;
}

function ChoicePill({ active, children, onClick, testid }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testid}
            className={`px-4 py-2 rounded-full border-2 font-bold text-sm transition-all ${
                active
                    ? "bg-[#2A1408] text-[#FFF1E0] border-[#2A1408]"
                    : "bg-[#FFF1E0] text-[#2A1408] border-[#2A1408] hover:bg-[#E4D1BB]"
            }`}
            style={{ fontFamily: "Bricolage Grotesque, sans-serif" }}
        >
            {active && <Check size={14} className="inline mr-1.5" strokeWidth={3} />}
            {children}
        </button>
    );
}

function GiantCookieOrder() {
    const [base, setBase] = useState("");
    const [nappage, setNappage] = useState("");
    const [toppings, setToppings] = useState([]);
    const [quantity, setQuantity] = useState(1);
    const [shop, setShop] = useState("macon");
    const [pickupDate, setPickupDate] = useState("");
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [phone, setPhone] = useState("");
    const [notes, setNotes] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const nappageObj = NAPPAGES.find((n) => n.id === nappage);
    const unitPrice = BASE_PRICE + (nappageObj?.extra || 0);
    const total = unitPrice * quantity;

    const toggleTopping = (t) => {
        setToppings((prev) => {
            if (prev.includes(t)) return prev.filter((x) => x !== t);
            if (prev.length >= MAX_TOPPINGS) {
                toast.error(`Maximum ${MAX_TOPPINGS} toppings.`);
                return prev;
            }
            return [...prev, t];
        });
    };

    const reset = () => {
        setBase("");
        setNappage("");
        setToppings([]);
        setQuantity(1);
        setNotes("");
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!base) {
            toast.error("Choisis une base.");
            return;
        }
        if (!nappage) {
            toast.error("Choisis un nappage.");
            return;
        }
        if (toppings.length === 0) {
            toast.error("Choisis au moins un topping.");
            return;
        }
        const baseLabel = BASES.find((b) => b.id === base)?.name || base;
        const message = [
            `Demande de Cookie/Brownie à partager (20 cm – 6/8 personnes) × ${quantity}`,
            `Boutique: ${shop}`,
            `Retrait souhaité: ${pickupDate || "non précisé"}`,
            "",
            `Quantité: ${quantity}`,
            `Base: ${baseLabel}`,
            `Nappage: ${nappageObj?.name || nappage}${nappageObj?.extra ? ` (+${nappageObj.extra}€)` : ""}`,
            `Toppings (${toppings.length}/${MAX_TOPPINGS}): ${toppings.join(", ")}`,
            "",
            `Prix unitaire: ${formatPrice(unitPrice)}`,
            `Total estimé: ${formatPrice(total)} (${quantity} × ${formatPrice(unitPrice)})`,
            "",
            `Notes: ${notes || "-"}`,
        ].join("\n");

        setSubmitting(true);
        try {
            await api.post("/contact", {
                name,
                email,
                phone,
                shop,
                subject: "Commande Cookie/Brownie à partager",
                message,
            });
            toast.success("Demande envoyée, on revient vers toi vite !");
            reset();
        } catch (_err) {
            toast.error("Erreur lors de l'envoi. Réessaye.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="grid lg:grid-cols-3 gap-10 mt-8 items-start" data-testid="giant-order">
            {/* CONFIGURATEUR */}
            <div className="lg:col-span-2 space-y-10">
                <div className="brand-card p-6">
                    <p className="font-handwritten text-3xl text-[#B07B66]">à partager</p>
                    <h3 className="font-display text-3xl text-[#2A1408] leading-none mt-1">
                        Le cookie ou brownie géant.
                    </h3>
                    <ul className="mt-4 space-y-1 text-sm text-[#3D1F0F]">
                        <li>• 20 cm de diamètre, environ 700 g</li>
                        <li>• 6 à 8 personnes</li>
                        <li>
                            • Prix de base{" "}
                            <span className="font-display text-xl text-[#D6552C] mr-2">
                                14 €
                            </span>
                            (suppléments au choix)
                        </li>
                        <li>• Commande 24h/48h à l'avance selon dispos</li>
                    </ul>
                </div>

                {/* STEP 1 */}
                <section data-testid="step-base">
                    <div className="flex items-baseline gap-3 mb-4">
                        <span className="font-display text-3xl text-[#D6552C]">01</span>
                        <h4 className="font-display text-2xl text-[#2A1408]">
                            Choisis ta base
                        </h4>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {BASES.map((b) => (
                            <ChoicePill
                                key={b.id}
                                active={base === b.id}
                                onClick={() => setBase(b.id)}
                                testid={`base-${b.id}`}
                            >
                                {b.name}
                            </ChoicePill>
                        ))}
                    </div>
                </section>

                {/* STEP 2 */}
                <section data-testid="step-nappage">
                    <div className="flex items-baseline gap-3 mb-4">
                        <span className="font-display text-3xl text-[#D6552C]">02</span>
                        <h4 className="font-display text-2xl text-[#2A1408]">
                            Choisis ton nappage
                        </h4>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {NAPPAGES.map((n) => (
                            <ChoicePill
                                key={n.id}
                                active={nappage === n.id}
                                onClick={() => setNappage(n.id)}
                                testid={`nappage-${n.id}`}
                            >
                                {n.name}
                                {n.extra > 0 && (
                                    <span className="ml-1 text-[#D6552C]">+{n.extra}€</span>
                                )}
                            </ChoicePill>
                        ))}
                    </div>
                </section>

                {/* STEP 3 */}
                <section data-testid="step-toppings">
                    <div className="flex items-baseline gap-3 mb-4 flex-wrap">
                        <span className="font-display text-3xl text-[#D6552C]">03</span>
                        <h4 className="font-display text-2xl text-[#2A1408]">
                            Choisis tes toppings
                        </h4>
                        <span className="text-sm text-[#5C3D2E]">
                            (jusqu'à {MAX_TOPPINGS}, sélectionnés : {toppings.length})
                        </span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {TOPPINGS.map((t) => (
                            <ChoicePill
                                key={t}
                                active={toppings.includes(t)}
                                onClick={() => toggleTopping(t)}
                                testid={`topping-${t}`}
                            >
                                {t}
                            </ChoicePill>
                        ))}
                    </div>
                </section>
            </div>

            {/* RECAP / FORM */}
            <form
                onSubmit={onSubmit}
                className="brand-card p-6 space-y-4 lg:sticky lg:top-32"
                data-testid="giant-order-form"
            >
                <h3 className="font-display text-2xl text-[#2A1408] flex items-center gap-2">
                    <ShoppingBag size={20} strokeWidth={2} /> Ta commande
                </h3>

                <div className="flex items-center justify-between gap-3 border-y-2 border-[#E4D1BB] py-3">
                    <span className="text-xs uppercase tracking-widest text-[#5C3D2E] font-bold">
                        Quantité
                    </span>
                    <div className="flex items-center gap-3">
                        <button
                            type="button"
                            aria-label="Diminuer"
                            onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                            className="w-9 h-9 rounded-full border-2 border-[#2A1408] text-[#2A1408] flex items-center justify-center hover:bg-[#2A1408] hover:text-[#FFF1E0] transition-colors disabled:opacity-40"
                            disabled={quantity <= 1}
                            data-testid="qty-dec"
                        >
                            <Minus size={16} strokeWidth={2.5} />
                        </button>
                        <span
                            className="font-display text-3xl text-[#2A1408] min-w-[3ch] text-center"
                            data-testid="qty-value"
                        >
                            {quantity}
                        </span>
                        <button
                            type="button"
                            aria-label="Augmenter"
                            onClick={() => setQuantity((q) => Math.min(99, q + 1))}
                            className="w-9 h-9 rounded-full bg-[#2A1408] text-[#FFF1E0] flex items-center justify-center hover:bg-[#6B4226] transition-colors"
                            data-testid="qty-inc"
                        >
                            <Plus size={16} strokeWidth={2.5} />
                        </button>
                    </div>
                </div>

                <div className="text-sm text-[#5C3D2E]">
                    Total estimé{" "}
                    <span className="font-display text-3xl text-[#D6552C]">
                        {formatPrice(total)}
                    </span>
                    {quantity > 1 && (
                        <span className="block text-xs mt-1">
                            ({quantity} × {formatPrice(unitPrice)})
                        </span>
                    )}
                </div>
                <label className="block text-xs uppercase tracking-widest text-[#5C3D2E]">
                    Boutique de retrait
                    <select
                        value={shop}
                        onChange={(e) => setShop(e.target.value)}
                        className="mt-1 w-full bg-white border border-[#E4D1BB] rounded-md px-3 py-2 text-sm"
                        data-testid="order-shop-select"
                    >
                        <option value="macon">Mâcon</option>
                        <option value="bourg-en-bresse">Bourg-en-Bresse</option>
                    </select>
                </label>
                <label className="block text-xs uppercase tracking-widest text-[#5C3D2E]">
                    Date souhaitée
                    <input
                        type="date"
                        value={pickupDate}
                        onChange={(e) => setPickupDate(e.target.value)}
                        className="mt-1 w-full bg-white border border-[#E4D1BB] rounded-md px-3 py-2 text-sm"
                        data-testid="order-date"
                    />
                </label>
                <label className="block text-xs uppercase tracking-widest text-[#5C3D2E]">
                    Nom complet
                    <input
                        type="text"
                        required
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="mt-1 w-full bg-white border border-[#E4D1BB] rounded-md px-3 py-2 text-sm"
                        data-testid="order-name"
                    />
                </label>
                <label className="block text-xs uppercase tracking-widest text-[#5C3D2E]">
                    Email
                    <input
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="mt-1 w-full bg-white border border-[#E4D1BB] rounded-md px-3 py-2 text-sm"
                        data-testid="order-email"
                    />
                </label>
                <label className="block text-xs uppercase tracking-widest text-[#5C3D2E]">
                    Téléphone
                    <input
                        type="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        className="mt-1 w-full bg-white border border-[#E4D1BB] rounded-md px-3 py-2 text-sm"
                        data-testid="order-phone"
                    />
                </label>
                <label className="block text-xs uppercase tracking-widest text-[#5C3D2E]">
                    Notes (allergies, occasion, etc.)
                    <textarea
                        rows={3}
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        className="mt-1 w-full bg-white border border-[#E4D1BB] rounded-md px-3 py-2 text-sm"
                        data-testid="order-notes"
                    />
                </label>
                <button
                    type="submit"
                    disabled={submitting}
                    className="brand-btn w-full justify-center"
                    data-testid="order-submit"
                >
                    <Send size={16} />
                    {submitting ? "Envoi…" : "Envoyer ma demande"}
                </button>
                <p className="text-xs text-[#5C3D2E]">
                    Pas de paiement en ligne, on confirme par téléphone ou email.
                </p>
            </form>
        </div>
    );
}

const NC_BASE_PRICE = 38;
const NC_CREME_NUTELLA_EXTRA = 2;
const NC_SUPPLEMENT_EXTRA = 4;

const NC_BASES = [
    { id: "brownie", name: "Brownie" },
    { id: "cookie-noir", name: "Cookie chocolat noir Nestlé" },
    { id: "cookie-lait", name: "Cookie chocolat au lait Milka" },
    { id: "cookie-blanc", name: "Cookie chocolat blanc Galak" },
];

const NC_CREMES = [
    { id: "vanille", name: "Vanille", extra: 0 },
    { id: "chocolat", name: "Chocolat", extra: 0 },
    { id: "nutella", name: "Nutella", extra: NC_CREME_NUTELLA_EXTRA },
];

const NC_SUPPLEMENTS = [
    "Kinder Maxi",
    "M&M's",
    "Cacahuètes",
    "Noix de pécan",
    "Noisettes",
    "Smarties",
    "Nutella",
    "Pâte à tartiner Spéculoos",
    "Beurre de cacahuète",
    "Oreo",
];

function NumberCakeOrder() {
    const [chiffres, setChiffres] = useState("");
    const [base, setBase] = useState("");
    const [creme, setCreme] = useState("");
    const [supplements, setSupplements] = useState([]);
    const [decoration, setDecoration] = useState("");
    const [quantity, setQuantity] = useState(1);
    const [shop, setShop] = useState("macon");
    const [pickupDate, setPickupDate] = useState("");
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [phone, setPhone] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const cremeObj = NC_CREMES.find((c) => c.id === creme);
    const supplementCost = supplements.length * NC_SUPPLEMENT_EXTRA;
    const unitPrice = NC_BASE_PRICE + (cremeObj?.extra || 0) + supplementCost;
    const total = unitPrice * quantity;

    const toggleSupplement = (s) => {
        setSupplements((prev) =>
            prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]
        );
    };

    const reset = () => {
        setChiffres("");
        setBase("");
        setCreme("");
        setSupplements([]);
        setDecoration("");
        setQuantity(1);
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!chiffres.trim()) {
            toast.error("Indique le chiffre ou la lettre.");
            return;
        }
        if (!base) {
            toast.error("Choisis une base.");
            return;
        }
        if (!creme) {
            toast.error("Choisis une crème.");
            return;
        }
        if (!pickupDate) {
            toast.error("Indique la date souhaitée.");
            return;
        }
        const baseLabel = NC_BASES.find((b) => b.id === base)?.name || base;
        const message = [
            `Demande de Number/Letter Cake (10 personnes) × ${quantity}`,
            `Boutique: ${shop}`,
            `Date souhaitée: ${pickupDate}`,
            "",
            `Chiffre / Lettre: ${chiffres}`,
            `Base: ${baseLabel}`,
            `Crème: ${cremeObj?.name || creme}${cremeObj?.extra ? ` (+${cremeObj.extra}€)` : ""}`,
            supplements.length > 0
                ? `Suppléments (${supplements.length} × +${NC_SUPPLEMENT_EXTRA}€): ${supplements.join(", ")}`
                : "Suppléments: aucun",
            "",
            `Décoration: ${decoration || "à voir avec le client"}`,
            "",
            `Prix unitaire: ${formatPrice(unitPrice)}`,
            `Total estimé: ${formatPrice(total)} (${quantity} × ${formatPrice(unitPrice)})`,
        ].join("\n");

        setSubmitting(true);
        try {
            await api.post("/contact", {
                name,
                email,
                phone,
                shop,
                subject: "Commande Number Cake",
                message,
            });
            toast.success("Demande envoyée, on revient vers toi vite !");
            reset();
        } catch (_err) {
            toast.error("Erreur lors de l'envoi.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="grid lg:grid-cols-3 gap-10 mt-8 items-start" data-testid="numbercake-order">
            <div className="lg:col-span-2 space-y-10">
                <div className="brand-card p-6">
                    <p className="font-handwritten text-3xl text-[#B07B66]">sur-mesure</p>
                    <h3 className="font-display text-3xl text-[#2A1408] leading-none mt-1">
                        Compose ton Number / Letter Cake.
                    </h3>
                    <ul className="mt-4 space-y-1 text-sm text-[#3D1F0F]">
                        <li>• 10 personnes environ</li>
                        <li>• Commande 14 jours à l'avance minimum</li>
                        <li>
                            • Prix de base{" "}
                            <span className="font-display text-xl text-[#D6552C] mr-2">
                                38 €
                            </span>
                            (suppléments au choix)
                        </li>
                    </ul>
                </div>

                <section data-testid="nc-step-chiffre">
                    <div className="flex items-baseline gap-3 mb-4">
                        <span className="font-display text-3xl text-[#D6552C]">00</span>
                        <h4 className="font-display text-2xl text-[#2A1408]">
                            Ton chiffre ou ta lettre
                        </h4>
                    </div>
                    <input
                        value={chiffres}
                        onChange={(e) => setChiffres(e.target.value)}
                        placeholder="ex: 30, 40, A, M, &…"
                        className="w-full max-w-sm bg-white border-2 border-[#2A1408] rounded-md px-4 py-3 text-lg font-display text-[#2A1408]"
                        data-testid="nc-chiffres"
                    />
                </section>

                <section data-testid="nc-step-base">
                    <div className="flex items-baseline gap-3 mb-4">
                        <span className="font-display text-3xl text-[#D6552C]">01</span>
                        <h4 className="font-display text-2xl text-[#2A1408]">
                            Choisis ta base
                        </h4>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {NC_BASES.map((b) => (
                            <ChoicePill
                                key={b.id}
                                active={base === b.id}
                                onClick={() => setBase(b.id)}
                                testid={`nc-base-${b.id}`}
                            >
                                {b.name}
                            </ChoicePill>
                        ))}
                    </div>
                </section>

                <section data-testid="nc-step-creme">
                    <div className="flex items-baseline gap-3 mb-4">
                        <span className="font-display text-3xl text-[#D6552C]">02</span>
                        <h4 className="font-display text-2xl text-[#2A1408]">
                            Choisis ta crème
                        </h4>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {NC_CREMES.map((c) => (
                            <ChoicePill
                                key={c.id}
                                active={creme === c.id}
                                onClick={() => setCreme(c.id)}
                                testid={`nc-creme-${c.id}`}
                            >
                                {c.name}
                                {c.extra > 0 && (
                                    <span className="ml-1 text-[#D6552C]">+{c.extra}€</span>
                                )}
                            </ChoicePill>
                        ))}
                    </div>
                </section>

                <section data-testid="nc-step-supplements">
                    <div className="flex items-baseline gap-3 mb-2 flex-wrap">
                        <span className="font-display text-3xl text-[#D6552C]">03</span>
                        <h4 className="font-display text-2xl text-[#2A1408]">
                            Supplément dans ta base
                        </h4>
                        <span className="text-sm text-[#5C3D2E]">
                            (facultatif · +{NC_SUPPLEMENT_EXTRA}€ par supplément)
                        </span>
                    </div>
                    <p className="text-xs text-[#5C3D2E] mb-3">
                        Sélectionne les saveurs que tu veux, chaque supplément ajoute
                        +{NC_SUPPLEMENT_EXTRA}€ au prix total.
                    </p>
                    <div className="flex flex-wrap gap-2">
                        {NC_SUPPLEMENTS.map((s) => (
                            <ChoicePill
                                key={s}
                                active={supplements.includes(s)}
                                onClick={() => toggleSupplement(s)}
                                testid={`nc-supp-${s}`}
                            >
                                {s}
                            </ChoicePill>
                        ))}
                    </div>
                </section>

                <section data-testid="nc-step-deco">
                    <div className="flex items-baseline gap-3 mb-2">
                        <span className="font-display text-3xl text-[#D6552C]">04</span>
                        <h4 className="font-display text-2xl text-[#2A1408]">
                            Choisis ta déco
                        </h4>
                    </div>
                    <p className="text-sm text-[#5C3D2E] mb-3">
                        Envoie-nous une photo, une idée, un thème (fruits, chocolat,
                        etc.). Décoration en pâte à sucre possible sur devis.
                    </p>
                    <textarea
                        value={decoration}
                        onChange={(e) => setDecoration(e.target.value)}
                        rows={4}
                        placeholder="Décris ton univers : couleurs, thème, fruits, photo Instagram, etc."
                        className="w-full bg-white border-2 border-[#E4D1BB] rounded-md px-3 py-2 text-sm"
                        data-testid="nc-decoration"
                    />
                </section>
            </div>

            <form
                onSubmit={onSubmit}
                className="brand-card p-6 space-y-4 lg:sticky lg:top-32"
                data-testid="numbercake-form"
            >
                <h3 className="font-display text-2xl text-[#2A1408] flex items-center gap-2">
                    <ShoppingBag size={20} strokeWidth={2} /> Ta commande
                </h3>

                <div className="flex items-center justify-between gap-3 border-y-2 border-[#E4D1BB] py-3">
                    <span className="text-xs uppercase tracking-widest text-[#5C3D2E] font-bold">
                        Quantité
                    </span>
                    <div className="flex items-center gap-3">
                        <button
                            type="button"
                            aria-label="Diminuer"
                            onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                            className="w-9 h-9 rounded-full border-2 border-[#2A1408] text-[#2A1408] flex items-center justify-center hover:bg-[#2A1408] hover:text-[#FFF1E0] transition-colors disabled:opacity-40"
                            disabled={quantity <= 1}
                            data-testid="nc-qty-dec"
                        >
                            <Minus size={16} strokeWidth={2.5} />
                        </button>
                        <span
                            className="font-display text-3xl text-[#2A1408] min-w-[3ch] text-center"
                            data-testid="nc-qty-value"
                        >
                            {quantity}
                        </span>
                        <button
                            type="button"
                            aria-label="Augmenter"
                            onClick={() => setQuantity((q) => Math.min(99, q + 1))}
                            className="w-9 h-9 rounded-full bg-[#2A1408] text-[#FFF1E0] flex items-center justify-center hover:bg-[#6B4226] transition-colors"
                            data-testid="nc-qty-inc"
                        >
                            <Plus size={16} strokeWidth={2.5} />
                        </button>
                    </div>
                </div>

                <div className="text-sm text-[#5C3D2E]">
                    Total estimé{" "}
                    <span className="font-display text-3xl text-[#D6552C]">
                        {formatPrice(total)}
                    </span>
                    {quantity > 1 && (
                        <span className="block text-xs mt-1">
                            ({quantity} × {formatPrice(unitPrice)})
                        </span>
                    )}
                </div>

                <label className="block text-xs uppercase tracking-widest text-[#5C3D2E]">
                    Boutique de retrait
                    <select
                        value={shop}
                        onChange={(e) => setShop(e.target.value)}
                        className="mt-1 w-full bg-white border border-[#E4D1BB] rounded-md px-3 py-2 text-sm"
                        data-testid="nc-shop"
                    >
                        <option value="macon">Mâcon</option>
                        <option value="bourg-en-bresse">Bourg-en-Bresse</option>
                    </select>
                </label>
                <label className="block text-xs uppercase tracking-widest text-[#5C3D2E]">
                    Date souhaitée
                    <input
                        type="date"
                        required
                        value={pickupDate}
                        onChange={(e) => setPickupDate(e.target.value)}
                        className="mt-1 w-full bg-white border border-[#E4D1BB] rounded-md px-3 py-2 text-sm"
                        data-testid="nc-date"
                    />
                </label>
                <label className="block text-xs uppercase tracking-widest text-[#5C3D2E]">
                    Nom complet
                    <input
                        type="text"
                        required
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="mt-1 w-full bg-white border border-[#E4D1BB] rounded-md px-3 py-2 text-sm"
                        data-testid="nc-name"
                    />
                </label>
                <label className="block text-xs uppercase tracking-widest text-[#5C3D2E]">
                    Email
                    <input
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="mt-1 w-full bg-white border border-[#E4D1BB] rounded-md px-3 py-2 text-sm"
                        data-testid="nc-email"
                    />
                </label>
                <label className="block text-xs uppercase tracking-widest text-[#5C3D2E]">
                    Téléphone
                    <input
                        type="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        className="mt-1 w-full bg-white border border-[#E4D1BB] rounded-md px-3 py-2 text-sm"
                        data-testid="nc-phone"
                    />
                </label>
                <button
                    type="submit"
                    disabled={submitting}
                    className="brand-btn w-full justify-center"
                    data-testid="nc-submit"
                >
                    <Send size={16} />
                    {submitting ? "Envoi…" : "Envoyer ma demande"}
                </button>
                <p className="text-xs text-[#5C3D2E]">
                    Pas de paiement en ligne, on confirme par téléphone ou email.
                </p>
            </form>
        </div>
    );
}

function CookiePersoOrder() {
    const [description, setDescription] = useState("");
    const [quantity, setQuantity] = useState(12);
    const [shop, setShop] = useState("macon");
    const [pickupDate, setPickupDate] = useState("");
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [phone, setPhone] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const reset = () => {
        setDescription("");
        setQuantity(12);
        setPickupDate("");
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!description.trim()) {
            toast.error("Décris ce que tu souhaites.");
            return;
        }
        if (!pickupDate) {
            toast.error("Indique la date souhaitée.");
            return;
        }
        const message = [
            `Demande de cookies personnalisés × ${quantity}`,
            `Boutique: ${shop}`,
            `Date souhaitée: ${pickupDate}`,
            "",
            "Brief / description:",
            description.trim(),
        ].join("\n");

        setSubmitting(true);
        try {
            await api.post("/contact", {
                name,
                email,
                phone,
                shop,
                subject: "Commande Cookies personnalisés",
                message,
            });
            toast.success("Demande envoyée, on revient vers toi vite !");
            reset();
        } catch (_err) {
            toast.error("Erreur lors de l'envoi.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="grid lg:grid-cols-3 gap-10 mt-8 items-start" data-testid="perso-order">
            <div className="lg:col-span-2 space-y-8">
                <div className="brand-card p-6">
                    <p className="font-handwritten text-3xl text-[#B07B66]">sur-mesure</p>
                    <h3 className="font-display text-3xl text-[#2A1408] leading-none mt-1">
                        Tes cookies, ton univers.
                    </h3>
                    <ul className="mt-4 space-y-1 text-sm text-[#3D1F0F]">
                        <li>• Baby shower, mariage, anniversaire thématique, EVJF/EVG…</li>
                        <li>• Décoration en pâte à sucre, prénoms, dates, logos perso</li>
                        <li>• Devis personnalisé selon le brief, on te recontacte sous 48 h</li>
                        <li>• Commande 7 à 14 jours à l'avance selon complexité</li>
                    </ul>
                </div>

                <section data-testid="perso-step-brief">
                    <div className="flex items-baseline gap-3 mb-3 flex-wrap">
                        <span className="font-display text-3xl text-[#D6552C]">01</span>
                        <h4 className="font-display text-2xl text-[#2A1408]">
                            Décris ton projet
                        </h4>
                    </div>
                    <p className="text-sm text-[#5C3D2E] mb-3">
                        Plus tu nous donnes de détails, plus on cible vite. Thème,
                        couleurs, prénoms, message à inscrire, photo d'inspi… Tout est
                        bienvenu, on adapte ensuite avec toi.
                    </p>
                    <textarea
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        rows={8}
                        placeholder={
                            "ex : Baby shower fille, couleurs rose poudré & blanc, motifs landau et pieds de bébé, prénom 'Lola' inscrit sur quelques pièces, livraison le samedi matin…"
                        }
                        className="w-full bg-white border-2 border-[#2A1408] rounded-md px-4 py-3 text-sm leading-relaxed"
                        data-testid="perso-description"
                    />
                </section>
            </div>

            <form
                onSubmit={onSubmit}
                className="brand-card p-6 space-y-4 lg:sticky lg:top-32"
                data-testid="perso-form"
            >
                <h3 className="font-display text-2xl text-[#2A1408] flex items-center gap-2">
                    <ShoppingBag size={20} strokeWidth={2} /> Ta demande
                </h3>

                <div className="flex items-center justify-between gap-3 border-y-2 border-[#E4D1BB] py-3">
                    <span className="text-xs uppercase tracking-widest text-[#5C3D2E] font-bold">
                        Quantité
                    </span>
                    <div className="flex items-center gap-3">
                        <button
                            type="button"
                            aria-label="Diminuer"
                            onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                            className="w-9 h-9 rounded-full border-2 border-[#2A1408] text-[#2A1408] flex items-center justify-center hover:bg-[#2A1408] hover:text-[#FFF1E0] transition-colors disabled:opacity-40"
                            disabled={quantity <= 1}
                            data-testid="perso-qty-dec"
                        >
                            <Minus size={16} strokeWidth={2.5} />
                        </button>
                        <span
                            className="font-display text-3xl text-[#2A1408] min-w-[3ch] text-center"
                            data-testid="perso-qty-value"
                        >
                            {quantity}
                        </span>
                        <button
                            type="button"
                            aria-label="Augmenter"
                            onClick={() => setQuantity((q) => Math.min(500, q + 1))}
                            className="w-9 h-9 rounded-full bg-[#2A1408] text-[#FFF1E0] flex items-center justify-center hover:bg-[#6B4226] transition-colors"
                            data-testid="perso-qty-inc"
                        >
                            <Plus size={16} strokeWidth={2.5} />
                        </button>
                    </div>
                </div>

                <p className="text-xs text-[#5C3D2E]">
                    Tarif établi sur devis selon le brief, on te confirme par téléphone
                    ou email.
                </p>

                <label className="block text-xs uppercase tracking-widest text-[#5C3D2E]">
                    Boutique de retrait
                    <select
                        value={shop}
                        onChange={(e) => setShop(e.target.value)}
                        className="mt-1 w-full bg-white border border-[#E4D1BB] rounded-md px-3 py-2 text-sm"
                        data-testid="perso-shop"
                    >
                        <option value="macon">Mâcon</option>
                        <option value="bourg-en-bresse">Bourg-en-Bresse</option>
                    </select>
                </label>
                <label className="block text-xs uppercase tracking-widest text-[#5C3D2E]">
                    Date souhaitée
                    <input
                        type="date"
                        required
                        value={pickupDate}
                        onChange={(e) => setPickupDate(e.target.value)}
                        className="mt-1 w-full bg-white border border-[#E4D1BB] rounded-md px-3 py-2 text-sm"
                        data-testid="perso-date"
                    />
                </label>
                <label className="block text-xs uppercase tracking-widest text-[#5C3D2E]">
                    Nom complet
                    <input
                        type="text"
                        required
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="mt-1 w-full bg-white border border-[#E4D1BB] rounded-md px-3 py-2 text-sm"
                        data-testid="perso-name"
                    />
                </label>
                <label className="block text-xs uppercase tracking-widest text-[#5C3D2E]">
                    Email
                    <input
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="mt-1 w-full bg-white border border-[#E4D1BB] rounded-md px-3 py-2 text-sm"
                        data-testid="perso-email"
                    />
                </label>
                <label className="block text-xs uppercase tracking-widest text-[#5C3D2E]">
                    Téléphone
                    <input
                        type="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        className="mt-1 w-full bg-white border border-[#E4D1BB] rounded-md px-3 py-2 text-sm"
                        data-testid="perso-phone"
                    />
                </label>
                <button
                    type="submit"
                    disabled={submitting}
                    className="brand-btn w-full justify-center"
                    data-testid="perso-submit"
                >
                    <Send size={16} />
                    {submitting ? "Envoi…" : "Envoyer ma demande"}
                </button>
                <p className="text-xs text-[#5C3D2E]">
                    Pas de paiement en ligne, on confirme par téléphone ou email.
                </p>
            </form>
        </div>
    );
}

export default function Commander() {
    return (
        <div className="max-w-6xl mx-auto px-6 sm:px-10 py-20" data-testid="commander-page">
            <Toaster richColors position="top-center" />
            <div className="text-center mb-12 relative">
                <span
                    className="hidden md:inline-block absolute top-2 right-10 sticker"
                    style={{ transform: "rotate(8deg)" }}
                >
                    Pas de paiement en ligne
                </span>
                <p className="section-tag">on a faim ?</p>
                <h1 className="font-display text-7xl md:text-9xl text-[#2A1408] leading-[0.85] mt-1">
                    On commande.
                </h1>
                <p className="mt-5 text-[#5C3D2E] max-w-2xl mx-auto text-lg">
                    Pour les anniversaires, évènements pros, baby showers, ou juste pour
                    se faire plaisir entre potes, on prépare des gourmandises XXL, à
                    partager.
                </p>
            </div>

            <Tabs defaultValue="cookies" className="w-full" data-testid="commander-tabs">
                <TabsList
                    className="bg-[#FFF1E0] border-2 border-[#2A1408] rounded-full p-1.5 h-auto mx-auto flex w-fit gap-1 flex-wrap"
                    style={{ boxShadow: "4px 4px 0 #2A1408" }}
                >
                    <TabsTrigger
                        value="cookies"
                        data-testid="tab-cookies"
                        className="rounded-full px-6 py-2.5 font-bold data-[state=active]:bg-[#2A1408] data-[state=active]:text-[#FFF1E0]"
                        style={{ fontFamily: "Bricolage Grotesque, sans-serif" }}
                    >
                        Cookie / Brownie XXL
                    </TabsTrigger>
                    <TabsTrigger
                        value="numbercake"
                        data-testid="tab-numbercake"
                        className="rounded-full px-6 py-2.5 font-bold data-[state=active]:bg-[#2A1408] data-[state=active]:text-[#FFF1E0]"
                        style={{ fontFamily: "Bricolage Grotesque, sans-serif" }}
                    >
                        Number Cake
                    </TabsTrigger>
                    <TabsTrigger
                        value="perso"
                        data-testid="tab-perso"
                        className="rounded-full px-6 py-2.5 font-bold data-[state=active]:bg-[#2A1408] data-[state=active]:text-[#FFF1E0]"
                        style={{ fontFamily: "Bricolage Grotesque, sans-serif" }}
                    >
                        Cookies personnalisés
                    </TabsTrigger>
                </TabsList>
                <TabsContent value="cookies">
                    <GiantCookieOrder />
                </TabsContent>
                <TabsContent value="numbercake">
                    <NumberCakeOrder />
                    <CreationsGallery
                        endpoint="/numbercake-gallery"
                        tagText="nos réalisations"
                        title="Number cakes faits maison."
                        subtitle="Quelques pièces sorties de l'atelier, pour piocher des idées de forme, de déco ou de thème pour ta commande."
                        testid="numbercake-gallery"
                    />
                </TabsContent>
                <TabsContent value="perso">
                    <CookiePersoOrder />
                    <CreationsGallery
                        endpoint="/cookie-perso-gallery"
                        tagText="nos réalisations"
                        title="Cookies personnalisés."
                        subtitle="Baby showers, mariages, anniversaires thématiques, on adapte la déco, le message, les couleurs. Envoie-nous ton brief."
                        testid="perso-gallery"
                    />
                </TabsContent>
            </Tabs>
        </div>
    );
}
